/**
 * TruePrice Canvas Navigation — CanvasBottomNav
 *
 * Bottom navigation with 3 tabs: Dashboard, Portfolio, Studio.
 * Active: thicker stroke (2.5) + teal. Inactive: thin stroke (1.5) + gray.
 */

import { type LucideIcon, Activity, LayoutGrid, TrendingUp } from 'lucide-react';
import { T } from '@/styles/tokens';

export const BOTTOM_NAV_HEIGHT = 72;

interface NavItemDef {
  id: string;
  label: string;
  Icon: LucideIcon;
  /** If true, tapping dispatches 'open-settings' event instead of page change */
  isOverlay?: boolean;
}

const navItems: NavItemDef[] = [
  { id: 'dashboard', label: 'Dashboard', Icon: Activity },
  { id: 'portfolio', label: 'Portfolio', Icon: LayoutGrid },
  { id: 'insights', label: 'Insights', Icon: TrendingUp },
];

interface CanvasBottomNavProps {
  currentPage: number;
  onPageSelect: (index: number) => void;
  className?: string;
  dashboardBadge?: number;
  portfolioBadge?: number;
}

function TabBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <div style={{
      position: 'absolute', top: -2, right: -6,
      width: 16, height: 16, borderRadius: 8,
      background: T.primary,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1,
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: T.white, lineHeight: 1 }}>
        {count > 9 ? '9+' : count}
      </span>
    </div>
  );
}

export function CanvasBottomNav({
  currentPage,
  onPageSelect,
  className = '',
  dashboardBadge = 0,
  portfolioBadge = 0,
}: CanvasBottomNavProps) {
  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-40 ${className}`}
      style={{
        height: BOTTOM_NAV_HEIGHT,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        background: 'rgba(250, 248, 243, 0.92)',
        borderTop: '1px solid rgba(0, 0, 0, 0.04)',
        boxShadow: 'none',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
      role="tablist"
      aria-label="Main navigation"
    >
      <div className="max-w-lg mx-auto h-full flex items-center justify-around px-4">
        {navItems.map((item, index) => {
          const isActive = !item.isOverlay && index === currentPage;

          return (
            <button
              key={item.id}
              onClick={() => {
                if (item.isOverlay) {
                  window.dispatchEvent(new Event('open-settings'));
                } else {
                  onPageSelect(index);
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 72,
                height: 56,
                padding: `0 ${T.md}`,
                borderRadius: T.radiusMd,
                border: 'none',
                background: 'transparent',
                color: isActive ? T.primary : T.textMuted,
                cursor: 'pointer',
                transition: 'color 150ms ease',
              }}
              role="tab"
              aria-selected={isActive}
              aria-label={item.label}
            >
              <div style={{ position: 'relative' }}>
                <item.Icon
                  className="w-5 h-5"
                  strokeWidth={isActive ? 2.5 : 1.5}
                />
                {item.id === 'dashboard' && <TabBadge count={dashboardBadge} />}
                {item.id === 'portfolio' && <TabBadge count={portfolioBadge} />}
              </div>
              <span style={{ fontSize: 10, marginTop: 4, fontWeight: isActive ? 700 : 500 }}>
                {item.label}
              </span>
              {isActive && (
                <span
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: T.primary,
                    marginTop: 4,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default CanvasBottomNav;
